import isNil from 'lodash/isNil';

/**
 * The jackpot pot, printed in full with thousands separators ("1,205,000") and never K/M abbreviated.
 *
 * Deliberately NOT `formatAmountByCoinType`, which abbreviates the GC/CC side to "487.15M": the number
 * under the crown is the advert, and the whole figure is the point.
 *
 * ## Whole numbers only
 * The pot is rounded to a whole figure. A bare `toLocaleString()` — which is what both widgets used
 * to call — prints up to THREE fraction digits and drops trailing zeros, so a pot ticking through
 * 487,154,146.5 then 487,154,146.567 rendered as `.5` then `.567`, changing width as it moved. This
 * prints neither, which also removes that width churn entirely.
 *
 * This is game-ui's `formatJackpotAmount` (`src/modules/jackpot/formatJackpotAmount.ts`), reproduced
 * because the two repos share no code. The popup's ladder, the in-slot banner and the lobby widgets are
 * the same pot seen from three surfaces, and they have to agree digit for digit.
 *
 * Anything unparseable passes through untouched rather than rendering as "NaN".
 */
export function formatJackpotAmount(amount: string | number | null | undefined): string {
  if (isNil(amount) || amount === '') {
    return '';
  }

  const value = typeof amount === 'number' ? amount : Number(amount);

  if (!Number.isFinite(value)) {
    return String(amount);
  }

  // NO DECIMALS. Every caller of this in player-frontend is a POT label — the category row's figure
  // — and a jackpot advert has no use for cents. (game-ui's copy still prints 2dp: there the same
  // function also formats `contributionPerRound`, which is about 0.1 and would render as "0", and
  // the win amount a player actually receives.)
  return Math.round(value).toLocaleString();
}

/** A surface's digit budget, straight from KRA-397's "up to N digits" rules. */
export type JackpotRoundingBudget = {
  /** A figure with up to this many integer digits prints in full. */
  fullUpToDigits: number;
  /** Up to this many digits prints as K; anything longer prints as M. */
  kUpToDigits: number;
};

/**
 * A pot within the caller's digit budget prints in full (via
 * {@link formatJackpotAmount}); past it, the pot rounds to K, then to M — grouped, one decimal at
 * most ("6,000,013.8K"), and M is the top tier no matter how large the figure. Every locale file
 * spells `numbers.thousand`/`numbers.million` as K/M, so the suffixes are literal here.
 *
 * Nil and unparseable input defer to {@link formatJackpotAmount} too. The category row never rounds
 * renders the plain formatter, not this one.
 */
export function formatJackpotAmountRounded(
  amount: string | number | null | undefined,
  { fullUpToDigits, kUpToDigits }: JackpotRoundingBudget,
): string {
  const value = typeof amount === 'number' ? amount : Number(amount);

  // Nil and unparseable defer, so '' stays '' and junk passes through rather than reading as "NaN".
  if (isNil(amount) || amount === '' || !Number.isFinite(value)) {
    return formatJackpotAmount(amount);
  }

  /*
   * NO DECIMALS on a pot, at either tier.
   *
   * A jackpot figure is an advert, not a statement of account: the cents under a nine-figure pot say
   * nothing a player acts on, and they cost characters in boxes already tight enough that a 10-digit
   * pot had to be abbreviated to fit. `5,994.2M` becomes `5,994M`, `300,720.62` becomes `300,721`.
   *
   * The full branch rounds HERE rather than deferring to {@link formatJackpotAmount}, which still
   * prints 2dp — that function also formats `contributionPerRound` (about 0.1, so 0dp would render
   * it as "0") and the win amount a player actually receives. Pots lose their decimals; money owed
   * to someone keeps them.
   */
  if (value < 10 ** fullUpToDigits) {
    return Math.round(value).toLocaleString();
  }

  const [divisor, suffix] = value < 10 ** kUpToDigits ? ([1_000, 'K'] as const) : ([1_000_000, 'M'] as const);

  return `${Math.round(value / divisor).toLocaleString()}${suffix}`;
}

export function isRoundedJackpotAmount(
  amount: string | number | null | undefined,
  { fullUpToDigits }: Pick<JackpotRoundingBudget, 'fullUpToDigits'>,
): boolean {
  const value = typeof amount === 'number' ? amount : Number(amount);

  if (isNil(amount) || amount === '' || !Number.isFinite(value)) {
    return false;
  }

  return value >= 10 ** fullUpToDigits;
}
